import { createSelector } from 'reselect';

import { CUSTOM_SEND_PRE_CREATED_ENGAGEMENT_ID } from '../../constants/bucket';
import { MESSAGE, NOTE_TEMPLATE_ID } from '../../constants/templates';
import { TEngagementCandidate } from '../../types/bucket';
import { EngagementSourceTypesEnum } from '../../types/reports';
import { ShippingOptionsEnum } from '../../types/shipping';
import { normalizeStateCode } from '../../utils/addressValidation';
import { buildLocationSendItems, TLocationContentItem } from '../../utils/createSendLocation';
import { selectCsIeItems, selectCsKits } from '../createSend/selectors/contents';
import { selectCsShipmentCrmLink, selectIsCsCrmOrg } from '../createSend/selectors/crm';
import {
  selectCsBusinessReasonId,
  selectCsDisableSenderEmails,
  selectCsInternalNotes,
} from '../createSend/selectors/sendSettings';
import { selectCsShipmentInfoForm } from '../createSend/selectors/shipmentInfo';
import { selectEffectiveCsSendSettings } from './createSendFlow';

export type TCreateSendLocationCandidate = TEngagementCandidate & { shipping_option: ShippingOptionsEnum };

// Location sends never carry marketplace content; reading IE-only items keeps a stale
// persisted pick from reaching the request.
export const selectCreateSendLocationCandidate = createSelector(
  selectCsKits,
  selectCsIeItems,
  selectCsShipmentInfoForm,
  selectCsBusinessReasonId,
  selectCsInternalNotes,
  selectCsDisableSenderEmails,
  selectEffectiveCsSendSettings,
  selectIsCsCrmOrg,
  selectCsShipmentCrmLink,
  (
    kits,
    items,
    form,
    businessReasonId,
    internalNotes,
    isSenderNotificationsDisabled,
    effectiveSettings,
    isCrmOrg,
    crmLink,
  ): TCreateSendLocationCandidate => ({
    receiver_first_name: form.firstName.trim(),
    receiver_last_name: form.lastName.trim(),
    receiver_email: form.email.trim(),
    receiver_phone: form.phone.trim(),
    receiver_address1: form.address1.trim(),
    receiver_address2: form.address2.trim(),
    receiver_city: form.city.trim(),
    receiver_state: normalizeStateCode(form.state, form.country),
    receiver_zip: form.zip.trim(),
    receiver_country: form.country.trim(),
    receiver_company_name: form.company.trim(),
    receiver_tax_number: '',
    ...(isCrmOrg
      ? {
          receiver_crm_record_id: crmLink?.recordId ?? '',
          receiver_crm_record_type: crmLink?.recordType ?? '',
          receiver_crm_opportunity_id: crmLink?.opportunityId ?? '',
        }
      : {}),
    status: '',
    is_receiver_address_fixed: true,
    is_office_dropship_enabled: false,
    shipping_option: ShippingOptionsEnum.Ground,
    engagement_source_type: EngagementSourceTypesEnum.DirectSend,
    [MESSAGE]: '',
    [NOTE_TEMPLATE_ID]: '',
    items: buildLocationSendItems(kits, items as TLocationContentItem[] | undefined),
    pre_created_engagement_id: CUSTOM_SEND_PRE_CREATED_ENGAGEMENT_ID,
    campaign_name: '',
    internal_notes: internalNotes,
    business_reason_id: businessReasonId,
    ...(effectiveSettings.isOnHold && effectiveSettings.onHoldUntil
      ? { on_hold_until: effectiveSettings.onHoldUntil }
      : {}),
    disable_sender_email_notifications: Boolean(isSenderNotificationsDisabled),
    disable_recipient_shipped_email: effectiveSettings.isRecipientShippedEmailDisabled,
  }),
);
